/**
 * Canonical front-end URLs for search results.
 *
 * Keep these in sync with the routes declared in `src/App.tsx`. Every result
 * link produced by `SearchService` goes through here so a single change fixes
 * the search bar, the dropdown and the /pesquisa page at once.
 */

/**
 * Directus `pages` whose slug is served by a dedicated top-level route
 * (e.g. `contactos` → `/contactos`). Anything not listed falls back to the
 * generic `/pages/:slug` route rendered by `DynamicPage`.
 */
export const TOP_LEVEL_PAGE_SLUGS: ReadonlySet<string> = new Set([
  'loja',
  'servicos',
  'noticias',
  'contactos',
  'suporte',
  'simulador',
  'file-service',
  'pesquisa',
  'sobre',
  'termos',
  'privacidade',
]);

/** Slugs that represent the home page. */
const HOME_PAGE_SLUGS: ReadonlySet<string> = new Set(['home', 'inicio', 'index']);

const normaliseSlug = (slug: string | null | undefined): string =>
  (slug ?? '').trim().replace(/^\/+|\/+$/g, '').toLowerCase();

export function getPageUrl(slug: string | null | undefined): string {
  const clean = normaliseSlug(slug);
  if (!clean || HOME_PAGE_SLUGS.has(clean)) return '/';
  if (TOP_LEVEL_PAGE_SLUGS.has(clean)) return `/${clean}`;
  return `/pages/${encodeURIComponent(clean)}`;
}

/** Services are listed by `Servicos.tsx` as `/servicos/:slug`. */
export function getServiceUrl(slug: string | null | undefined): string {
  const clean = normaliseSlug(slug);
  return clean ? `/servicos/${encodeURIComponent(clean)}` : '/servicos';
}

/** Product detail is routed by id (`/loja/:id`); product slugs are mostly null in prod. */
export function getProductUrl(id: string | number | null | undefined): string {
  return id === null || id === undefined || id === '' ? '/loja' : `/loja/${encodeURIComponent(String(id))}`;
}

export function getNewsUrl(id: string | number | null | undefined): string {
  return id === null || id === undefined || id === '' ? '/noticias' : `/noticias/${encodeURIComponent(String(id))}`;
}

/**
 * For the legacy `/pages/:slug` route: the canonical URL when it differs
 * (so old links redirect to `/contactos` etc.), otherwise `null`.
 */
export function getCanonicalPageRedirect(slug: string | null | undefined): string | null {
  const url = getPageUrl(slug);
  return url.startsWith('/pages/') ? null : url;
}
